import { type ChangeEvent, type SubmitEvent, useState } from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import {
  WEB_ROUTES,
  formatActivationCode,
  formatActivationCodeInput,
  isValidActivationCodeSyntax,
} from '@lsc/shared';
import { PageHeading } from '../components/PageHeading';

const ACTIVATION_PATH = /\/a\/([^/?#\s]+)/u;

/**
 * Formats raw input while typing/pasting. Accepts the full activation URL from the letter
 * (`https://…/a/K7PF2-9MQDX`) as well as spaces, dashes and lower case.
 */
export function formatCodeEntry(raw: string): string {
  const fromUrl = ACTIVATION_PATH.exec(raw)?.[1];
  return formatActivationCodeInput(fromUrl ?? raw);
}

const SYNTAX_ERROR =
  'Bitte geben Sie den vollständigen Code mit 10 Zeichen ein (Format XXXXX-XXXXX).';

/** `/` – manual code entry (§49). The client check is UX only; the server decides (§95). */
export function CodeEntryPage() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    setCode(formatCodeEntry(event.target.value));
    setError(null);
  }

  function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!isValidActivationCodeSyntax(code)) {
      setError(SYNTAX_ERROR);
      return;
    }
    void navigate(WEB_ROUTES.activation(formatActivationCode(code)));
  }

  return (
    <Stack spacing={3}>
      <Box>
        <PageHeading>Zugang aktivieren</PageHeading>
        <Typography color="text.secondary">
          Geben Sie den Aktivierungscode aus Ihrem Schreiben ein. Alternativ können Sie den QR-Code
          mit der Kamera Ihres Smartphones scannen.
        </Typography>
      </Box>
      <Box component="form" noValidate onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <TextField
            id="activation-code"
            label="Aktivierungscode"
            value={code}
            onChange={handleChange}
            error={error !== null}
            helperText={
              error ?? 'Format XXXXX-XXXXX. Codes enthalten kein O, I, L sowie keine 0 und 1.'
            }
            placeholder="XXXXX-XXXXX"
            autoComplete="off"
            slotProps={{
              htmlInput: {
                inputMode: 'text',
                autoCapitalize: 'characters',
                autoCorrect: 'off',
                spellCheck: false,
                enterKeyHint: 'go',
                sx: {
                  fontSize: { xs: '1.5rem', sm: '1.75rem' },
                  fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
                  letterSpacing: '0.12em',
                  py: 1.75,
                },
              },
            }}
          />
          <Button type="submit" variant="contained" size="large">
            Code prüfen
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}
